"use client";

import { useState, useRef, useEffect } from "react";
import { motion, useInView, useReducedMotion } from "@/lib/motion";
import { inViewScrollRootMargin } from "@/lib/motion-viewport";
import { cn } from "@/lib/utils";
import { Check, X } from "@/components/icons";
import { IP_METRICS, MONTHS } from "./data";

export function IPComparisonPanel() {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref as React.RefObject<Element>, {
    once: true,
    margin: inViewScrollRootMargin,
  });
  const prefersReducedMotion = useReducedMotion();
  const [scanLine, setScanLine] = useState(0);

  useEffect(() => {
    if (prefersReducedMotion) return;
    const id = setInterval(() => setScanLine((s) => (s + 1) % IP_METRICS.length), 2200);
    return () => clearInterval(id);
  }, [prefersReducedMotion]);

  return (
    <div ref={ref} className="border-border bg-card overflow-hidden rounded-2xl border">
      <div className="border-border flex items-center justify-between border-b px-5 py-4">
        <p className="text-foreground text-sm font-semibold">IP Quality Benchmark</p>
        <span
          className="text-muted-foreground text-[10px] font-bold tracking-wider uppercase"
          title="Representative figures for illustration, not live measured data"
        >
          Illustrative
        </span>
      </div>

      <div className="p-4 sm:p-5">
        <div className="mb-2 grid grid-cols-[0.8fr_1fr_1fr] gap-2 px-1.5">
          <div />
          <p className="text-muted-foreground text-center text-[10px] font-bold tracking-wider uppercase">
            Datacenter
          </p>
          <p
            className="text-center text-[10px] font-bold tracking-wider uppercase"
            style={{ color: "var(--brand-green)" }}
          >
            Proxidize
          </p>
        </div>

        <div className="space-y-1">
          {IP_METRICS.map((metric, i) => {
            const isActive = scanLine === i;
            // Bars show effective IP quality, so "lower is better" metrics
            // (e.g. detection rate) are inverted before rendering.
            const datacenterQuality = metric.lowerIsBetter
              ? 100 - metric.datacenter.score
              : metric.datacenter.score;
            const residentialQuality = metric.lowerIsBetter
              ? 100 - metric.residential.score
              : metric.residential.score;

            return (
              <motion.div
                key={metric.label}
                initial={{ opacity: 0, x: -16 }}
                animate={inView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.35, delay: i * 0.08 }}
                className={cn(
                  "grid grid-cols-[0.8fr_1fr_1fr] items-stretch gap-2 rounded-xl p-1.5 transition-colors duration-500",
                  isActive && "bg-foreground/[0.03]"
                )}
              >
                <div
                  className={cn(
                    "flex items-center pr-1 text-[11px] leading-tight font-medium transition-colors duration-500",
                    isActive ? "text-foreground" : "text-muted-foreground"
                  )}
                >
                  {metric.label}
                </div>

                <div
                  className={cn(
                    "border-border bg-muted/20 flex flex-col items-center justify-center gap-1 rounded-lg border p-2.5 text-center transition-colors duration-500",
                    isActive && "border-foreground/15"
                  )}
                >
                  <span className="text-muted-foreground flex items-center gap-1 text-[11px] font-bold">
                    <X size={11} className="text-muted-foreground shrink-0" aria-hidden="true" />
                    {metric.datacenter.value}
                  </span>
                  <div className="bg-muted relative flex h-1 w-full items-center overflow-x-hidden rounded-full">
                    <motion.div
                      className="bg-muted-foreground/30 h-full rounded-full"
                      animate={inView ? { width: `${datacenterQuality}%` } : { width: "0%" }}
                      transition={{ duration: 0.7, delay: 0.3 + i * 0.08 }}
                    />
                  </div>
                </div>

                <div
                  className="flex flex-col items-center justify-center gap-1 rounded-lg border p-2.5 text-center transition-colors duration-500"
                  style={{
                    borderColor: isActive
                      ? "color-mix(in srgb, var(--brand-green) 35%, transparent)"
                      : "color-mix(in srgb, var(--brand-green) 12%, transparent)",
                    backgroundColor: "color-mix(in srgb, var(--brand-green) 6%, transparent)",
                  }}
                >
                  <span
                    className="flex items-center gap-1 text-[11px] font-bold"
                    style={{ color: "var(--brand-green)" }}
                  >
                    <Check size={11} className="shrink-0" aria-hidden="true" />
                    {metric.residential.value}
                  </span>
                  <div
                    className="relative flex h-1 w-full items-center overflow-x-hidden rounded-full"
                    style={{
                      backgroundColor: "color-mix(in srgb, var(--brand-green) 12%, transparent)",
                    }}
                  >
                    <motion.div
                      className="h-full rounded-full"
                      style={{ backgroundColor: "var(--brand-green)" }}
                      animate={inView ? { width: `${residentialQuality}%` } : { width: "0%" }}
                      transition={{ duration: 0.7, delay: 0.3 + i * 0.08 }}
                    />
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      <div className="border-border border-t px-5 py-3.5">
        <p className="text-muted-foreground text-[11px] leading-relaxed">
          Real residential IPs blend into everyday user traffic — datacenter ranges are flagged on
          sight by major targets. Figures above are illustrative, based on general industry
          detection patterns for residential vs. datacenter IP ranges rather than a live-measured
          Proxidize benchmark.
        </p>
      </div>
    </div>
  );
}

export function BandwidthRolloverVisual() {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref as React.RefObject<Element>, {
    once: true,
    margin: inViewScrollRootMargin,
  });
  const [activeMonth, setActiveMonth] = useState(3);

  const month = MONTHS[activeMonth];
  const available = month.purchased + month.rollover;
  const usedPct = Math.round((month.used / available) * 100);
  const rolloverPct = Math.round((month.rollover / available) * 100);
  const newPct = Math.round((month.purchased / available) * 100);

  return (
    <div ref={ref} className="space-y-4">
      <div className="grid grid-cols-4 gap-2">
        {MONTHS.map((m, i) => (
          <button
            key={m.label}
            onClick={() => setActiveMonth(i)}
            className={cn(
              "rounded-lg border px-1 py-2 text-xs font-bold transition-all",
              activeMonth === i
                ? ""
                : "text-muted-foreground border-border bg-card hover:border-border/80"
            )}
            style={
              activeMonth === i
                ? {
                    backgroundColor: "color-mix(in srgb, var(--brand-green) 10%, transparent)",
                    borderColor: "color-mix(in srgb, var(--brand-green) 30%, transparent)",
                    color: "var(--brand-green)",
                  }
                : {}
            }
          >
            {m.label}
          </button>
        ))}
      </div>

      <motion.div
        key={activeMonth}
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.22 }}
        className="border-border bg-card rounded-xl border p-5"
      >
        <div className="mb-1 flex items-baseline justify-between">
          <span className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
            Total Available
          </span>
          <span className="text-2xl font-bold tabular-nums" style={{ color: "var(--brand-green)" }}>
            {available} GB
          </span>
        </div>

        <div className="my-3 flex h-3 gap-0.5 overflow-hidden rounded-full">
          <motion.div
            className="bg-foreground/8 h-full rounded-l-full"
            animate={inView ? { width: `${usedPct}%` } : { width: "0%" }}
            transition={{ duration: 0.6, delay: 0.1 }}
          />
          {rolloverPct > 0 && (
            <motion.div
              className="bg-foreground/[22%] h-full"
              animate={inView ? { width: `${rolloverPct}%` } : { width: "0%" }}
              transition={{ duration: 0.6, delay: 0.2 }}
            />
          )}
          <motion.div
            className="h-full rounded-r-full"
            style={{ backgroundColor: "var(--brand-green)" }}
            animate={inView ? { width: `${newPct}%` } : { width: "0%" }}
            transition={{ duration: 0.6, delay: 0.3 }}
          />
        </div>

        <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px]">
          <span className="text-muted-foreground flex items-center gap-1.5">
            <span className="bg-foreground/8 inline-block h-2.5 w-2.5 rounded-sm" />
            Used ({month.used} GB)
          </span>
          <span className="text-muted-foreground flex items-center gap-1.5">
            <span className="bg-foreground/[22%] inline-block h-2.5 w-2.5 rounded-sm" />
            Rolled over ({month.rollover} GB)
          </span>
          <span className="text-muted-foreground flex items-center gap-1.5">
            <span
              className="inline-block h-2.5 w-2.5 rounded-sm"
              style={{ backgroundColor: "var(--brand-green)" }}
            />
            New this month ({month.purchased} GB)
          </span>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.4, delay: 0.45 }}
        className="relative overflow-hidden rounded-xl border p-4"
        style={{
          borderColor: "color-mix(in srgb, var(--brand-green) 18%, transparent)",
          backgroundColor: "color-mix(in srgb, var(--brand-green) 4%, transparent)",
        }}
      >
        <p
          className="mb-2 text-[10px] font-bold tracking-widest uppercase"
          style={{ color: "var(--brand-green)" }}
        >
          Your bandwidth never expires.
        </p>
        <div className="flex flex-wrap gap-1.5">
          {[
            "Unused GB rolls over",
            "Cancellation-safe",
            "No forced expiry",
            "Use at your convenience",
          ].map((tag, i) => (
            <motion.span
              key={tag}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={inView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.25, delay: 0.55 + i * 0.06 }}
              className="rounded-full border px-2.5 py-1 text-[10px] font-semibold"
              style={{
                color: "var(--brand-green)",
                borderColor: "color-mix(in srgb, var(--brand-green) 25%, transparent)",
                backgroundColor: "color-mix(in srgb, var(--brand-green) 7%, transparent)",
              }}
            >
              {tag}
            </motion.span>
          ))}
        </div>
      </motion.div>
    </div>
  );
}
